import { useState, useEffect } from 'react';
import SlidePanel from './SlidePanel';
import AddPersonModal from '../modals/AddPersonModal';
import PassLevelMultiSelect from './PassLevelMultiSelect';
import { usePassLevels } from '../../hooks/usePassLevels';
import { upsertPerson, deletePerson, invalidateCache, getPersonSchedule, getServingAvailabilityRequest, setKidsAvailability, getCheckIns, getCheckInData } from '../../utils/api';
import { personScheduleUrl } from '../../utils/scheduleFormat';

// The fixed Kids session grid — mirrors KIDS_SESSION_SLOTS in the Apps Script.
const KIDS_SESSIONS = [
  { key: 'Thursday|Evening', title: 'Session 1', day: 'Thursday', period: 'Evening' },
  { key: 'Friday|Morning',   title: 'Session 2', day: 'Friday',   period: 'Morning' },
  { key: 'Friday|Evening',   title: 'Session 3', day: 'Friday',   period: 'Evening' },
  { key: 'Saturday|Morning', title: 'Session 4', day: 'Saturday', period: 'Morning' },
  { key: 'Saturday|Evening', title: 'Session 5', day: 'Saturday', period: 'Evening' },
];

// BG-check dates arrive as raw Date toString junk ("Wed Jul 29 2026
// 00:00:00 GMT-0500 (…)") — show plain yyyy-mm-dd like the check-in pill.
function fmtShortDate(v) {
  if (!v) return '';
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (isNaN(d)) return s;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtT(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  if (Number.isNaN(h)) return t;
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

export default function PersonPanel({ open, onClose, person, showToast, onPassLevelUpdated, onPersonUpdated }) {
  const passLevels = usePassLevels();
  const [passLevel, setPassLevel] = useState(person?.passLevel || '');
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  // Cross-department commitments — only shown for genuine both-servers so a lead
  // on one side can see (read-only) what the person is doing on the other (§6).
  const [crossItems, setCrossItems] = useState([]);
  const [myBlocks, setMyBlocks] = useState([]);   // real per-block assignment statuses
  const [serving, setServing] = useState(null);    // real submitted serving availability
  // Kids availability editing (staff fix-ups: wrong-person form submissions,
  // phoned-in answers, "just give them open availability").
  const [kaEdit, setKaEdit] = useState(false);
  const [kaMax, setKaMax] = useState(0);
  const [kaSel, setKaSel] = useState([]);
  const [kaSaving, setKaSaving] = useState(false);
  const [kaLocal, setKaLocal] = useState(null);    // saved override so the panel shows the new values immediately
  useEffect(() => { setKaEdit(false); setKaLocal(null); }, [person?.id]);
  // Door check-in record (date + time), from the CheckIns sheet.
  const [checkInRec, setCheckInRec] = useState(null);
  // The same audited meal grid the check-in card shows (Thu–Sun × B/L/D),
  // fetched per person from the deployed check-in endpoint.
  const [mealGrid, setMealGrid] = useState(null);
  const [mealGridLoading, setMealGridLoading] = useState(false);
  useEffect(() => {
    setMealGrid(null);
    if (!open || !person?.id || person.id === 'demo') return;
    let cancelled = false;
    setMealGridLoading(true);
    getCheckInData('', person.id)
      .then(d => { if (!cancelled) setMealGrid(d?.results?.[0]?.meals || null); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setMealGridLoading(false); });
    return () => { cancelled = true; };
  }, [open, person?.id]);
  useEffect(() => {
    setCheckInRec(null);
    if (!open || !person?.id || person.id === 'demo') return;
    let cancelled = false;
    getCheckIns()
      .then(m => { if (!cancelled) setCheckInRec((m || {})[person.id] || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, person?.id]);

  // Fetch the person's real schedule + serving availability when the panel
  // opens. GAS can take 10s+ under conference load, so the section shows an
  // honest "Loading…" (not the empty state) until this actually resolves,
  // and offers Retry when it fails.
  const [schedLoading, setSchedLoading] = useState(false);
  const [schedError, setSchedError] = useState(false);
  const [schedReloadKey, setSchedReloadKey] = useState(0);
  useEffect(() => {
    setCrossItems([]);
    setMyBlocks([]);
    setServing(null);
    setSchedError(false);
    if (!open || !person?.id || person.id === 'demo') return;
    let cancelled = false;
    setSchedLoading(true);
    getPersonSchedule(person.id)
      .then(d => {
        if (cancelled) return;
        const all = d?.schedule || [];
        const items = all.filter(s => !s.isMeal && s.status !== 'unsent' && s.status !== 'declined');
        const depts = new Set(items.map(s => s.department).filter(Boolean));
        // Only surface for both-servers; single-department people are unchanged.
        setCrossItems(depts.size > 1 ? items : []);
        // Real block-by-block status: only genuine assignments carry an assignmentId.
        setMyBlocks(all.filter(s => s.assignmentId));
      })
      .catch(() => { if (!cancelled) setSchedError(true); })
      .finally(() => { if (!cancelled) setSchedLoading(false); });
    // Kids people answer session-based availability instead of the main
    // day×time grid — that data is injected as person._kidsAvail, no fetch.
    if (!person._isKids) {
      getServingAvailabilityRequest(person.id)
        .then(d => { if (!cancelled) setServing(d); })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [open, person?.id, person?._isKids, schedReloadKey]);

  // Reset the remove flow whenever a different person is opened, so a stale
  // confirm/"Removing…" state from a prior person never carries over.
  useEffect(() => {
    setConfirmRemove(false);
    setRemoving(false);
  }, [person?.id]);

  // Injected by the team-lead roster (see TeamLeadView) so this panel can
  // actually remove the person from that team. Absent in contexts (e.g. admin
  // People) where there's no single team in scope.
  async function handleRemove() {
    if (!person?._onRemoveFromTeam) {
      // Admin context (no team roster behind this panel): delete the person
      // outright. The backend refuses when they still fill scheduled
      // positions — and says exactly why — so nothing scheduled can vanish.
      setRemoving(true);
      try {
        await deletePerson(person.id);
        showToast && showToast(`${person.name} deleted. Refresh to update the list.`);
        setRemoving(false);
        onClose();
      } catch (err) {
        console.error('Failed to delete person:', err);
        showToast && showToast(err.message || 'Could not delete this person.');
        setRemoving(false);
      }
      return;
    }
    setRemoving(true);
    try {
      await person._onRemoveFromTeam();
      setRemoving(false);
      onClose();
    } catch (err) {
      console.error('Failed to remove person from team:', err);
      showToast && showToast('Error removing person.');
      setRemoving(false);
    }
  }

  async function handlePassLevelChange(newLevel) {
    setPassLevel(newLevel);
    try {
      // passLevelManual locks this value so the team auto-sync never overrides it
      await upsertPerson({ ...person, passLevel: newLevel, passLevelManual: true });
      invalidateCache('people');
      showToast && showToast('Pass level updated');
      onPassLevelUpdated && onPassLevelUpdated(person.id, newLevel);
    } catch (err) {
      console.error('Failed to update pass level:', err);
      showToast && showToast('Error updating pass level');
    }
  }

  // Keep the pass dropdown in sync if the person record changes (e.g. after an edit).
  useEffect(() => { setPassLevel(person?.passLevel || ''); }, [person?.passLevel]);

  async function handleEditSave(updated) {
    try {
      await upsertPerson(updated);
      invalidateCache('people');
      setPassLevel(updated.passLevel || '');
      onPersonUpdated && onPersonUpdated(updated);
      showToast && showToast('Person updated.');
    } catch (err) {
      console.error('Failed to update person:', err);
      showToast && showToast('Error updating person.');
    }
  }

  if (!person) return null;

  return (
    <SlidePanel
      open={open}
      onClose={onClose}
      title={person.name}
      subtitle={person.team || 'No team assigned'}
    >
      <div className="slide-section-title">Contact</div>
      <div className="detail-row">
        <span className="detail-label">Email</span>
        <span className="detail-val">{person.email || '—'}</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">Phone</span>
        <span className="detail-val">{person.phone || '—'}</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">Pass Level</span>
        <PassLevelMultiSelect
          value={passLevel}
          options={passLevels}
          onChange={v => handlePassLevelChange(v)}
        />
      </div>
      <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
        <span className="detail-label">Meals</span>
        {mealGridLoading ? (
          <span style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)' }}>Loading meals…</span>
        ) : mealGrid ? (
          <table className="checkin-meal-table">
            <thead>
              <tr>
                <th></th>
                {['Thu', 'Fri', 'Sat', 'Sun'].map(d => <th key={d}>{d}</th>)}
              </tr>
            </thead>
            <tbody>
              {['Breakfast', 'Lunch', 'Dinner'].map(m => (
                <tr key={m}>
                  <td className="cmt-day">{m}</td>
                  {['Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                    <td key={d} className="cmt-cell">
                      {mealGrid?.[d]?.[m] ? (
                        <svg viewBox="0 0 14 14" fill="none" style={{ width: 14, height: 14 }}>
                          <path d="M2.5 7.5L5.5 10.5L11.5 4" stroke="#202020" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      ) : <span className="cmt-empty" />}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <span style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)' }}>No meal data</span>
        )}
      </div>
      <div className="detail-row">
        <span className="detail-label">Registration</span>
        <span>
          {person.registered
            ? <span className="pill pill-green" style={{ fontSize: 9, padding: '3px 9px' }}>Registered</span>
            : <span className="pill pill-amber" style={{ fontSize: 9, padding: '3px 9px' }}>Not Registered</span>
          }
        </span>
      </div>
      <div className="detail-row">
        <span className="detail-label">Checked In</span>
        <span>
          {checkInRec ? (
            <span className="pill pill-green" style={{ fontSize: 9, padding: '3px 9px' }}>
              ✓ {checkInRec.date}{checkInRec.time ? ` · ${checkInRec.time}` : ''}
            </span>
          ) : (
            <span style={{ fontSize: 12, color: 'var(--subtle)' }}>Not checked in</span>
          )}
        </span>
      </div>
      <div className="detail-row">
        <span className="detail-label">Background Check</span>
        <span>
          {/clear/i.test(person.bgCheck || '') ? (
            <span className="pill pill-green" style={{ fontSize: 9, padding: '3px 9px' }}>✓ Clear{person.bgCheckDate ? ` · ${fmtShortDate(person.bgCheckDate)}` : ''}</span>
          ) : person.bgCheck ? (
            <span className="pill pill-amber" style={{ fontSize: 9, padding: '3px 9px' }}>{person.bgCheck}</span>
          ) : (
            <span className="pill" style={{ fontSize: 9, padding: '3px 9px', background: '#fee', color: '#b00000', border: '1px solid #f5c2c2' }}>No BG Check</span>
          )}
        </span>
      </div>
      {person.mealsOnly && (
        <div className="detail-row">
          <span className="detail-label">Meals</span>
          <span className="pill" style={{ fontSize: 9, padding: '3px 9px', background: '#F2DADA', color: '#8B3A3A' }}>Meals only · all days</span>
        </div>
      )}

      {crossItems.length > 0 && (
        <>
          <div className="slide-section-title">Also Serving (read-only)</div>
          {crossItems.map((s, i) => (
            <div className="detail-row" key={i}>
              <span className="detail-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span className="pill" style={{ fontSize: 9, padding: '2px 7px', background: s.department === 'Kids' ? '#6AA292' : '#202020', color: '#fff' }}>
                  {s.department || 'Main'}
                </span>
                {s.teamName}
              </span>
              <span className="detail-val" style={{ fontWeight: 300, color: 'var(--muted)' }}>
                {s.day}{s.startTime ? ` · ${fmtT(s.startTime)}` : ''}
              </span>
            </div>
          ))}
        </>
      )}

      <div className="slide-section-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>Serving Availability</span>
        {person._isKids && !kaEdit && (
          <button
            className="btn btn-xs"
            title="Edit this person's session availability by hand"
            onClick={() => {
              const ka = kaLocal || person._kidsAvail || {};
              setKaMax(ka.maxSessions || 0);
              setKaSel(ka.selected || []);
              setKaEdit(true);
            }}
          >
            Edit
          </button>
        )}
      </div>
      {person._isKids ? (() => {
        // Kids: "most helpful 3" — a max session count + the sessions they're
        // free for, mirroring the fixed 5-session grid (see KIDS_SESSION_SLOTS).
        if (kaEdit) {
          const toggle = (k) => setKaSel(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]);
          const save = async () => {
            setKaSaving(true);
            try {
              await setKidsAvailability({ personId: person.id, teamId: person._kidsTeamId || 'Kids', maxSessions: kaMax, selected: kaSel });
              setKaLocal({ maxSessions: kaMax, selected: kaSel });
              setKaEdit(false);
              if (showToast) showToast('Availability updated.');
            } catch (e) {
              console.error('Failed to save kids availability:', e);
              if (showToast) showToast('Failed to save availability.');
            }
            setKaSaving(false);
          };
          return (
            <>
              <div className="detail-row" style={{ alignItems: 'center', gap: 8 }}>
                <span className="detail-label">Can serve up to</span>
                <select
                  className="form-input"
                  style={{ width: 64, fontSize: 13, padding: '4px 8px', height: 'auto' }}
                  value={kaMax}
                  onChange={e => setKaMax(Number(e.target.value))}
                >
                  {[0, 1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <span className="detail-val">sessions</span>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                {KIDS_SESSIONS.map(s => {
                  const on = kaSel.includes(s.key);
                  return (
                    <button
                      key={s.key}
                      onClick={() => toggle(s.key)}
                      style={{ fontFamily: 'var(--font-h)', fontSize: 10, fontWeight: 700, padding: '5px 9px', borderRadius: 4, border: `1px solid ${on ? '#2D6B55' : 'var(--border)'}`, cursor: 'pointer', background: on ? '#D4E8DF' : 'var(--off)', color: on ? '#2D6B55' : 'var(--subtle)' }}
                    >
                      {s.title} · {s.day.slice(0, 3)} {s.period}
                    </button>
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button className="btn btn-xs btn-dark" disabled={kaSaving} onClick={save}>{kaSaving ? 'Saving…' : 'Save'}</button>
                <button className="btn btn-xs" disabled={kaSaving} onClick={() => setKaEdit(false)}>Cancel</button>
              </div>
            </>
          );
        }
        const ka = kaLocal || person._kidsAvail;
        if (!ka || (!ka.maxSessions && !(ka.selected || []).length)) {
          return (
            <div className="detail-row">
              <span className="detail-val" style={{ color: 'var(--muted)', fontWeight: 300 }}>Not submitted yet</span>
            </div>
          );
        }
        const sel = new Set(ka.selected || []);
        return (
          <>
            <div className="detail-row">
              <span className="detail-label">Can serve</span>
              <span className="detail-val">up to {ka.maxSessions || '?'} session{ka.maxSessions === 1 ? '' : 's'}</span>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
              {KIDS_SESSIONS.map(s => {
                const on = sel.has(s.key);
                return (
                  <div key={s.key} style={{ fontFamily: 'var(--font-h)', fontSize: 10, fontWeight: 700, padding: '5px 9px', borderRadius: 4, background: on ? '#D4E8DF' : 'var(--off)', color: on ? '#2D6B55' : 'var(--subtle)' }}>
                    {s.title} · {s.day.slice(0, 3)} {s.period}
                  </div>
                );
              })}
            </div>
          </>
        );
      })() : (() => {
        const sel = new Set(serving?.existing?.selected || []);
        const days = serving?.days || [];
        const slots = serving?.slots || [];
        if (!serving || sel.size === 0) {
          return (
            <div className="detail-row">
              <span className="detail-val" style={{ color: 'var(--muted)', fontWeight: 300 }}>
                {serving ? 'Not submitted yet' : 'Loading…'}
              </span>
            </div>
          );
        }
        return (
          <>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {days.map(day => (
                <div key={day} style={{ flex: '1 1 56px', minWidth: 56 }}>
                  <div style={{ fontFamily: 'var(--font-h)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--muted)', marginBottom: 4, textAlign: 'center' }}>
                    {day.slice(0, 3)}
                  </div>
                  {slots.map(slot => {
                    const on = sel.has(`${day}|${slot.key}`);
                    return (
                      <div key={slot.key} style={{ fontFamily: 'var(--font-h)', fontSize: 10, fontWeight: 700, textAlign: 'center', padding: '4px 0', marginBottom: 3, borderRadius: 4, background: on ? '#D4E8DF' : 'var(--off)', color: on ? '#2D6B55' : 'var(--subtle)' }}>
                        {slot.key}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
            {serving?.existing?.note && (
              <div style={{ marginTop: 8, fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, fontStyle: 'italic', color: 'var(--mid)' }}>
                “{serving.existing.note}”
              </div>
            )}
          </>
        );
      })()}

      <div className="slide-section-title">Scheduling Status</div>
      {schedLoading ? (
        <div className="detail-row">
          <span className="detail-val" style={{ color: 'var(--muted)', fontWeight: 300 }}>Loading blocks…</span>
        </div>
      ) : schedError ? (
        <div className="detail-row" style={{ gap: 10 }}>
          <span className="detail-val" style={{ color: '#900', fontWeight: 300 }}>Couldn't load the schedule.</span>
          <button className="btn btn-xs" onClick={() => setSchedReloadKey(k => k + 1)}>Retry</button>
        </div>
      ) : myBlocks.length === 0 ? (
        <div className="detail-row">
          <span className="detail-val" style={{ color: 'var(--muted)', fontWeight: 300 }}>No blocks assigned yet</span>
        </div>
      ) : (() => {
        // Group by team so a glance shows WHERE each block lives — bold
        // all-caps team header, then that team's blocks (+ position) under it.
        const teamOrder = [];
        const byTeam = {};
        myBlocks.forEach(b => {
          const t = b.teamName || 'No Team';
          if (!byTeam[t]) { byTeam[t] = []; teamOrder.push(t); }
          byTeam[t].push(b);
        });
        return teamOrder.map(team => (
          <div key={team}>
            <div style={{ fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 900, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--black)', padding: '14px 0 2px', borderBottom: '2px solid var(--black)', marginBottom: 2 }}>
              {team}
            </div>
            {byTeam[team].map((block, i) => (
              <div className="detail-row" key={block.assignmentId || i}>
                <span className="detail-label">
                  {block.blockTitle}{block.day ? ` · ${block.day}` : ''}
                  {block.positionLabel && (
                    <span style={{ display: 'block', fontFamily: 'var(--font-b)', fontWeight: 300, fontSize: 12, color: 'var(--muted)', textTransform: 'none', letterSpacing: 0, marginTop: 2 }}>
                      {block.positionLabel}
                    </span>
                  )}
                </span>
                <span>
                  {block.status === 'accepted' && <span className="pill pill-green" style={{ fontSize: 9 }}>Accepted</span>}
                  {block.status === 'pending' && <span className="pill pill-amber" style={{ fontSize: 9 }}>Pending</span>}
                  {block.status === 'declined' && <span className="pill" style={{ fontSize: 9, background: '#fee', color: '#900', border: '1px solid #fcc' }}>Declined</span>}
                  {(!block.status || block.status === 'unsent') && <span className="pill pill-gray" style={{ fontSize: 9 }}>Not Sent</span>}
                </span>
              </div>
            ))}
          </div>
        ));
      })()}

      <div className="slide-section-title">Registration</div>
      <div className="detail-row">
        <span className="detail-label">Brushfire ID</span>
        <span className="detail-val">{person.brushfireId || '—'}</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">Schedule Link</span>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            className="copy-link-btn"
            onClick={() => {
              navigator.clipboard.writeText(personScheduleUrl(person.id));
              showToast && showToast('Link copied to clipboard');
            }}
          >
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
              <rect x="4.5" y="4.5" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M3 8.5H2a1 1 0 01-1-1V2a1 1 0 011-1h5.5a1 1 0 011 1v1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
            Copy Link
          </button>
          <a
            href={`/s/${person.id || 'demo'}`}
            target="_blank"
            rel="noopener noreferrer"
            className="copy-link-btn"
            style={{ textDecoration: 'none' }}
          >
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
              <path d="M5 2H2a1 1 0 00-1 1v8a1 1 0 001 1h8a1 1 0 001-1v-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              <path d="M8 1h4v4M12 1L7 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Preview
          </a>
        </div>
      </div>

      <div style={{ marginTop: 24, display: 'flex', gap: 10 }}>
        <button className="btn btn-sm" onClick={() => setEditOpen(true)}>
          Edit Person
        </button>
        {confirmRemove ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#fff5f5', border: '1px solid #fcc', borderRadius: 'var(--radius)' }}>
            <span style={{ fontFamily: 'var(--font-b)', fontSize: 13, fontWeight: 500, color: 'var(--black)', flex: 1 }}>Remove {person.name}?</span>
            <button className="btn btn-xs" onClick={() => setConfirmRemove(false)} disabled={removing}>Cancel</button>
            <button className="btn btn-xs btn-danger" onClick={handleRemove} disabled={removing}>
              {removing ? 'Removing…' : 'Remove'}
            </button>
          </div>
        ) : (
          <button className="btn btn-sm btn-danger" onClick={() => setConfirmRemove(true)}>
            {person._onRemoveFromTeam ? 'Remove from Team' : 'Delete Person'}
          </button>
        )}
      </div>

      <AddPersonModal
        open={editOpen}
        person={person}
        onClose={() => setEditOpen(false)}
        onSave={handleEditSave}
        showToast={showToast}
      />
    </SlidePanel>
  );
}
